import { useEffect, useState, useCallback, useRef } from 'react';
import { fabric } from 'fabric';
import { ShapePoint, AnnotationsForGrid, MLRequest } from '../../Annotation/annotation.types';
import { getCurrentZoom, adjustStrokeToZoom, dirtyIndicatorColor, indicatorColor, getStrokeColorForAnnotation } from '../Helpers';
import { ValidationModalDto } from '../../Annotation/AnnotationValidationModal/AnnotationValidationModal.slice';
import {
  prepareMLrequest,
  getPoints,
  generateRectFromPoint,
  RawPoint,
  validateAnnotationShape,
  getBackgroundImage,
  generateAndRenderObjectsForNewPoint,
  generateAndRenderPolygonObject,
  clearPolygonElementsFromCanvas,
  isOutsideOfObjects,
  onInitialPoint,
  isEmptyFieldInPolygonMode,
  canDrawRect,
  getModifiedObjectPoints,
  canPanCanvas,
  moveRect,
  updateEndLinePoint
} from './Helpers';
import useCanvasOutsideEvents from './useCanvasOutsideEvents';
import { AnnotatedFinding } from '../prepareShapesSelector';
import { AnnotationStandardDetails } from '../../AnnotationStandardForm/AnnotationStandardForm.slice';
import { useKeyPress } from '../../../Shared/UseKeyPress';

const FAR_RIGHT_OVERLAY_POSITION = 230;

const useCanvasEvents = (
  canvasEl: fabric.Canvas | null,
  polygonMode: boolean,
  rectangleMode: boolean,
  annotations: AnnotationsForGrid[],
  onGeneratePolygon: (mode: boolean) => void,
  onGenerateRectangle: (mode: boolean) => void,
  onSaveActiveShape: (shape: ShapePoint[], imageScale: number, id: string) => void,
  onAnnotationEdit: (record: AnnotationsForGrid) => void,
  scaledOverlapping: { x: number; y: number }[][],
  scale: number,
  loadImageLocalization: (mlRequest: MLRequest, dto: ValidationModalDto, isUpdate?: boolean) => void,
  onSelectAnnotation: (key: string) => void,
  fullScreenMode: boolean,
  shapes: AnnotatedFinding[] | null,
  onFindingShapeChanged: (findingId: string) => void,
  presentAnnotations: AnnotationsForGrid[] | null,
  saveFinding: boolean,
  annotationStandardDetails: AnnotationStandardDetails | null,
  setHoveredFinding: (hoverd: any | undefined) => void,
  setShowFindingOverlayOnLeft: (onLeft: boolean) => void,
  onFindingSave: (saveFinding: boolean) => void,
  multiSelectObjects: string[],
  setMultiSelectObjects: (value: string[]) => void
) => {
  const [pointArray, setPointArray] = useState<fabric.Circle[]>([]);
  const [lineArray, setLineArray] = useState<fabric.Line[]>([]);
  const [activeLine, setActiveLine] = useState<fabric.Line | null>();
  const [activeShape, setActiveShape] = useState<fabric.Polygon | null>();
  const [isPanning, setIsPanning] = useState(false);

  const [origX, setOrigX] = useState<number>();
  const [origY, setOrigY] = useState<number>();
  const [rect, setRect] = useState<fabric.Rect | null>();

  const addPoint = useCallback(
    (options: fabric.IEvent) => {
      if (!canvasEl) return;

      const { polygon, line, circle } = generateAndRenderObjectsForNewPoint(canvasEl, options, pointArray, activeShape);

      setActiveShape(polygon);
      setActiveLine(line);
      setPointArray([...pointArray, circle]);
      setLineArray([...lineArray, line]);
    },
    [activeShape, canvasEl, lineArray, pointArray]
  );

  const prepareAndLoadLocalization = useCallback(
    (rawPoints: RawPoint[], backgroundImage: fabric.Object, isUpdate: boolean = false) => {
      const validationResult = validateAnnotationShape(rawPoints, backgroundImage, scaledOverlapping);
      const mlRequest = prepareMLrequest(rawPoints, backgroundImage.scaleX!);
      loadImageLocalization(mlRequest, validationResult, isUpdate);
    },
    [loadImageLocalization, scaledOverlapping]
  );

  const clearHelperFabricObjects = useCallback(() => {
    setActiveShape(null);
    setActiveLine(null);
    setLineArray([]);
    setPointArray([]);
  }, []);

  const generateRectangle = useCallback(() => {
    if (!canvasEl) return;

    canvasEl.add(rect!);
    const points = getPoints(rect!);
    const backgroundImage = getBackgroundImage(canvasEl);
    setRect(null);
    onGenerateRectangle(false);
    onSaveActiveShape(points, backgroundImage.scaleX!, rect!.name!);

    prepareAndLoadLocalization(points, backgroundImage);
  }, [canvasEl, onGenerateRectangle, onSaveActiveShape, prepareAndLoadLocalization, rect]);

  const generatePolygon = useCallback(() => {
    if (!canvasEl || !activeShape || !activeLine || pointArray.length < 3) return;

    const { points, polygonName } = generateAndRenderPolygonObject(canvasEl, pointArray, lineArray, activeLine, activeShape);

    clearHelperFabricObjects();

    const backgroundImage = getBackgroundImage(canvasEl);

    onGeneratePolygon(false);
    onSaveActiveShape(points, backgroundImage.scaleX!, polygonName);

    prepareAndLoadLocalization(points, backgroundImage);
  }, [
    canvasEl,
    activeLine,
    activeShape,
    lineArray,
    pointArray,
    clearHelperFabricObjects,
    onGeneratePolygon,
    onSaveActiveShape,
    prepareAndLoadLocalization
  ]);

  const clearPolygon = useCallback(() => {
    if (!canvasEl) return;

    clearPolygonElementsFromCanvas(canvasEl, pointArray, lineArray, activeLine, activeShape);
    clearHelperFabricObjects();
  }, [activeLine, activeShape, canvasEl, clearHelperFabricObjects, lineArray, pointArray]);

  const updateFinding = useCallback(
    (name: string, clearSelectedAnnotation: boolean = true) => {
      if (!canvasEl) return;

      const updatedAnnotation = presentAnnotations?.find(x => x.id === name);
      if (!(updatedAnnotation && updatedAnnotation.dirty)) {
        return;
      }

      const activeObject = canvasEl.getObjects().find(x => x.name === name)!;

      let transformedPoints = getModifiedObjectPoints(activeObject);

      let annotation = annotations.find(x => x.id === activeObject.name);
      if (annotation) {
        const backgroundImage = getBackgroundImage(canvasEl);

        let updatedObj = {
          ...annotation,
          shape: transformedPoints,
          imageScale: backgroundImage.scaleX!
        };
        onAnnotationEdit(updatedObj);

        prepareAndLoadLocalization(transformedPoints, backgroundImage, true);

        if (clearSelectedAnnotation) {
          onSelectAnnotation('');
        }
      }
    },
    [annotations, canvasEl, onAnnotationEdit, onSelectAnnotation, prepareAndLoadLocalization, presentAnnotations]
  );

  useEffect(() => {
    if (saveFinding && canvasEl) {
      const activeObject = canvasEl.getActiveObject();
      if (activeObject) {
        updateFinding(activeObject.name!);
      } else {
        onFindingSave(false);
      }
    }
  }, [canvasEl, onFindingSave, saveFinding, updateFinding]);

  useEffect(() => {
    if (shapes) {
      setIsPanning(false);
      if (canvasEl) {
        canvasEl.selection = false;
      }
    }
  }, [canvasEl, shapes]);

  useCanvasOutsideEvents(canvasEl, pointArray, lineArray, activeLine, activeShape, polygonMode, rectangleMode, fullScreenMode, clearPolygon, setRect);

  useEffect(() => {
    if (canvasEl && shapes) {
      const shapeIds = shapes.map(x => x.id);
      canvasEl.getObjects().forEach(x => {
        if (x.name && shapeIds.includes(x.name)) {
          x.evented = !(polygonMode || rectangleMode);
        }
      });
      canvasEl.renderAll();
    }
  }, [canvasEl, polygonMode, rectangleMode, shapes]);

  const fakeSelectionClear = useRef<boolean>(false);

  const ctrl = useKeyPress('Control', true);
  const ctrlA = useKeyPress('a', true);
  const shift = useKeyPress('Shift', false);
  const plus = useKeyPress('+', false);
  const minus = useKeyPress('-', false);

  useEffect(() => {
    if (!canvasEl) return;

    if (shift && plus) {
      const zoom = getCurrentZoom(canvasEl);
      canvasEl.setZoom(zoom + 0.1);
      adjustStrokeToZoom(canvasEl, zoom);
      return;
    }

    if (shift && minus) {
      const zoom = getCurrentZoom(canvasEl);
      canvasEl.setZoom(zoom - 0.5);
      adjustStrokeToZoom(canvasEl, zoom);
      return;
    }
  }, [canvasEl, minus, plus, shift]);

  useEffect(() => {
    if (!canvasEl) {
      return;
    }

    if (!multiSelectObjects.length) {
      const annotationObjects = canvasEl.getObjects('polygon').filter(x => annotations.map(x => x.id).includes(x.name!));
      annotationObjects.forEach(obj => {
        const annotationData = annotations.find(x => x.id === obj.name);

        if (!annotationData) {
          return;
        }

        obj.stroke = getStrokeColorForAnnotation(annotationData);
        obj.cornerColor = getStrokeColorForAnnotation(annotationData);
      });

      return;
    }

    const annotationObjects = canvasEl.getObjects('polygon').filter(x => annotations.map(x => x.id).includes(x.name!));
    const objects = canvasEl.getObjects('polygon').filter(x => multiSelectObjects.includes(x.name!));
    annotationObjects.forEach(obj => {
      const annotationData = annotations.find(x => x.id === obj.name);

      if (!annotationData) {
        return;
      }

      obj.stroke = getStrokeColorForAnnotation(annotationData);
    });
    objects.forEach(obj => {
      obj.stroke = indicatorColor;
      obj.strokeWidth = 1.5;
      obj.cornerColor = 'transparent';
    });
    canvasEl.requestRenderAll();
  }, [annotations, canvasEl, multiSelectObjects]);

  useEffect(() => {
    if (ctrlA && canvasEl) {
      const annotationObjects = canvasEl.getObjects('polygon').filter(x => annotations.find(y => y.id === x.name));

      const activeObjects = canvasEl.getActiveObjects();
      if (activeObjects && activeObjects.length) {
        const activeIds = canvasEl.getActiveObjects().map(x => x.name!);
        if (activeIds.length === annotationObjects.length) {
          return;
        }
      }

      let selection = new fabric.ActiveSelection(annotationObjects, { canvas: canvasEl });
      canvasEl.setActiveObject(selection);
      canvasEl.requestRenderAll();
    }
  }, [annotations, canvasEl, ctrlA]);

  useEffect(() => {
    if (!canvasEl) {
      return;
    }

    canvasEl.defaultCursor = ctrl ? 'default' : 'grab';
    canvasEl.requestRenderAll();
  }, [canvasEl, ctrl]);

  useEffect(() => {
    if (!canvasEl) return;

    canvasEl.selection = rectangleMode;

    if (ctrl) {
      canvasEl.selection = true;
      canvasEl.selectionBorderColor = 'red';
    } else {
      canvasEl.selectionBorderColor = 'blue';
    }

    canvasEl
      .on('mouse:down', e => {
        if (isOutsideOfObjects(e, polygonMode, rectangleMode)) {
          onSelectAnnotation('');
        }

        if (onInitialPoint(e, polygonMode, pointArray)) {
          generatePolygon();
        }

        if (isEmptyFieldInPolygonMode(e, polygonMode)) {
          addPoint(e);
        }

        if (canDrawRect(rectangleMode, canvasEl)) {
          const { newRect, origXNew, origYNew } = generateRectFromPoint(e, canvasEl);

          setOrigX(origXNew);
          setOrigY(origYNew);
          setRect(newRect);
        }

        if (ctrl && e.target && e.target.name && annotations.map(x => x.id).includes(e.target.name)) {
          if (multiSelectObjects.includes(e.target.name)) {
            const newSelectedObjectIds = multiSelectObjects.filter(x => x !== e.target!.name);
            setMultiSelectObjects(newSelectedObjectIds);

            if (newSelectedObjectIds.length) {
              const annotationObjects = canvasEl.getObjects('polygon').filter(x => newSelectedObjectIds.includes(x.name!));
              canvasEl.setActiveObject(annotationObjects[0]);
            }
          } else {
            setMultiSelectObjects([...multiSelectObjects, e.target.name]);
          }
        } else {
          setMultiSelectObjects([]);
        }

        if (ctrl) {
          return;
        }

        setIsPanning(true);
      })
      .on('mouse:wheel', opt => {
        const event = opt.e as WheelEvent;
        const zoom = getCurrentZoom(canvasEl, event);
        canvasEl!.zoomToPoint(new fabric.Point(event.offsetX, event.offsetY), zoom);
        canvasEl.calcOffset();

        opt.e.preventDefault();
        opt.e.stopPropagation();

        adjustStrokeToZoom(canvasEl, zoom);
      })
      .on('mouse:up', e => {
        setIsPanning(false);
        canvasEl.selection = false;

        if (rect) {
          generateRectangle();
        }

        const zoom = getCurrentZoom(canvasEl);

        if (!multiSelectObjects.length) {
          adjustStrokeToZoom(canvasEl, zoom);
        }
      })
      .on('mouse:move', e => {
        if (canPanCanvas(isPanning, e, canvasEl, polygonMode, rectangleMode)) {
          const mouseEvent = e.e as MouseEvent;
          var delta = new fabric.Point(mouseEvent.movementX, mouseEvent.movementY);
          canvasEl.relativePan(delta);
          canvasEl!.renderAll();
          return;
        }

        //This is probably redudant, because we only have polygons, only while creating
        if (isPanning && rectangleMode && rect) {
          moveRect(canvasEl, e, origX, origY, rect);
          setRect(rect);

          canvasEl.renderAll();
        }

        if (activeLine && activeLine.name === 'line') {
          updateEndLinePoint(canvasEl, e, activeLine, activeShape, pointArray);
        }

        canvasEl.renderAll();
      })
      .on('object:scaling', e => {
        var obj = canvasEl.getActiveObject();
        if (obj && obj.type === 'rect') {
          (obj as any).noScaleCache = false;
          obj.set({
            width: obj.width! * obj.scaleX!,
            height: obj.height! * obj.scaleY!,
            scaleX: 1,
            scaleY: 1
          });
        }
      })
      .on('object:modified', e => {
        const activeObject = canvasEl.getActiveObject();
        activeObject.stroke = dirtyIndicatorColor;
        onFindingShapeChanged(activeObject.name!);
      })
      .on('selection:created', opt => {
        const activeObject = canvasEl.getActiveObject();

        if (activeObject.type === 'activeSelection') {
          fakeSelectionClear.current = true;
          setMultiSelectObjects(canvasEl.getActiveObjects().map(x => x.name!));

          canvasEl.setActiveObject(canvasEl.getActiveObjects()[0]);
          return;
        }

        onSelectAnnotation(activeObject.name!);
      })
      .on('selection:updated', opt => {
        if (multiSelectObjects.length) {
          return;
        }
        const deselectedFinding = (opt as any).deselected;
        if (deselectedFinding) {
          updateFinding(deselectedFinding[0].name, false);
        }

        const activeObject = canvasEl.getActiveObject();
        onSelectAnnotation(activeObject.name!);
      })
      .on('selection:cleared', opt => {
        if (fakeSelectionClear.current) {
          fakeSelectionClear.current = false;
          return;
        }

        setMultiSelectObjects([]);

        if (multiSelectObjects.length) {
          return;
        }

        const deselectedFinding = (opt as any).deselected;
        if (deselectedFinding) {
          updateFinding(deselectedFinding[0].name);
        }
      })
      .on('mouse:out', opt => {
        setHoveredFinding(undefined);
        const activeObject = canvasEl.getActiveObject();
        if (!opt.target && activeObject) {
          updateFinding(activeObject.name!, false);
        }
      })
      .on('mouse:over', opt => {
        if (ctrl) {
          return;
        }

        if (opt && opt.target) {
          const hoverdFinding = annotations.find((x: any) => x.id === opt.target!.name!);
          if (annotationStandardDetails && annotationStandardDetails.name.includes('SGRE')) {
            setHoveredFinding(hoverdFinding);
            setShowFindingOverlayOnLeft((opt.e as MouseEvent).offsetX! > FAR_RIGHT_OVERLAY_POSITION);
          }
        }
      });

    return () => {
      canvasEl
        .off('mouse:over')
        .off('mouse:down')
        .off('mouse:move')
        .off('mouse:up')
        .off('mouse:wheel')
        .off('mouse:out')
        .off('object:scaling')
        .off('object:modified')
        .off('selection:created')
        .off('selection:cleared')
        .off('selection:updated');
    };
  }, [
    canvasEl,
    activeLine,
    activeShape,
    pointArray,
    isPanning,
    polygonMode,
    rectangleMode,
    rect,
    origX,
    origY,
    annotations,
    generatePolygon,
    addPoint,
    onGenerateRectangle,
    loadImageLocalization,
    onSaveActiveShape,
    onAnnotationEdit,
    onSelectAnnotation,
    prepareAndLoadLocalization,
    generateRectangle,
    updateFinding,
    onFindingShapeChanged,
    setHoveredFinding,
    annotationStandardDetails,
    setShowFindingOverlayOnLeft,
    ctrl,
    multiSelectObjects
  ]);
};

export default useCanvasEvents;
